import { useEffect, useRef, useState } from "react";
import { apiRequest } from "../../services/api";
const indiaDate = () =>
  new Date(Date.now() + 19800000).toISOString().slice(0, 10);
const fields = {
  invoiceNumber: "Full supplier invoice number",
  invoiceDate: "Full invoice date",
  supplierGstin: "Invoice supplier GSTIN",
  taxableAmount: "Actual invoice taxable amount",
  cgst: "Actual invoice CGST",
  sgst: "Actual invoice SGST",
  igst: "Actual invoice IGST",
  evidenceReference: "Full invoice evidence reference",
  reviewNote: "Full invoice review",
  reason: "Document correction / release reason",
};
const amount = (value) =>
  /^\d{1,14}(\.\d{1,2})?$/.test(value)
    ? window.BigInt(value.split(".")[0]) * 100n +
      window.BigInt((value.split(".")[1] || "").padEnd(2, "0"))
    : null;
const money = (n) => `${n / 100n}.${String(n % 100n).padStart(2, "0")}`;
export default function SisGrossInvoiceCredits({
  base,
  record,
  invoices,
  accessToken,
  allowed,
  canReverse,
  blocked,
  onSaved,
}) {
  const [open, setOpen] = useState(false),
    [evidence, setEvidence] = useState([]),
    [selected, setSelected] = useState([]),
    [invoiceId, setInvoiceId] = useState("");
  const [form, setForm] = useState({
    invoiceNumber: "",
    invoiceDate: indiaDate(),
    supplierGstin: "",
    taxableAmount: "",
    cgst: "0",
    sgst: "0",
    igst: "0",
    evidenceReference: "",
    reviewNote: "",
    reason: "",
  });
  const [proposal, setProposal] = useState(null),
    [checks, setChecks] = useState({}),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const pending = useRef(false),
    requestKey = useRef(null),
    alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const chosen = invoices.find((i) => i.id === invoiceId),
    bill = record.snapshot.bills[0];
  const parts = ["taxableAmount", "cgst", "sgst", "igst"].map((k) =>
      amount(form[k]),
    ),
    gross = parts.every((n) => n !== null)
      ? parts.reduce((s, n) => s + n, 0n)
      : null;
  const credits = selected.reduce(
      (s, id) =>
        s +
        (amount(String(evidence.find((m) => m.id === id)?.amount || "0")) ||
          0n),
      0n,
    ),
    net = gross !== null && gross > credits ? money(gross - credits) : "";
  function invalidate() {
    setProposal(null);
    setChecks({});
    requestKey.current = null;
    setError("");
    setNotice("");
  }
  function selectInvoice(id) {
    setInvoiceId(id);
    invalidate();
    const invoice = invoices.find((i) => i.id === id);
    setSelected(invoice?.snapshot.creditMatches?.map((m) => m.matchId) || []);
    if (invoice)
      setForm((f) => ({
        ...f,
        ...Object.fromEntries(
          Object.keys(fields)
            .filter(
              (k) => !["reason", "reviewNote", "evidenceReference"].includes(k),
            )
            .map((k) => [k, invoice.snapshot[k] || ""]),
        ),
        reason: "",
        reviewNote: "",
        evidenceReference: "",
      }));
  }
  async function load() {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError("");
    invalidate();
    try {
      const r = await apiRequest(
        `${base}/invoice-credit-evidence?accrualId=${encodeURIComponent(record.id)}`,
        { accessToken },
      );
      if (alive.current) {
        setEvidence(r.matches);
        setSelected([]);
      }
    } catch (e) {
      if (alive.current) setError(e.message);
    } finally {
      pending.current = false;
      if (alive.current) setBusy(false);
    }
  }
  async function preview(mode) {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    invalidate();
    try {
      const input =
        mode === "release"
          ? { date: indiaDate(), reason: form.reason }
          : {
              ...form,
              supplierId: bill.supplierId,
              registrationId: record.snapshot.registrationId,
              allocations: [
                { accrualId: record.id, billNumber: bill.number, amount: net },
              ],
              creditMatchIds: selected,
              ...(invoiceId ? { date: indiaDate() } : {}),
            };
      if (mode !== "release" && !invoiceId) delete input.reason;
      const url =
        mode === "release"
          ? `${base}/invoice-credit-evidence/${invoiceId}/release-preview`
          : invoiceId
            ? `${base}/invoices/${invoiceId}/replacement-preview`
            : `${base}/invoices/preview`;
      const r = await apiRequest(url, {
        method: "POST",
        accessToken,
        body: input,
      });
      if (alive.current) setProposal({ ...r, input, mode, invoiceId });
    } catch (e) {
      if (alive.current) setError(e.message);
    } finally {
      pending.current = false;
      if (alive.current) setBusy(false);
    }
  }
  async function save() {
    if (
      pending.current ||
      !proposal ||
      !allowed ||
      ![checks.one, checks.two, checks.three].every(Boolean) ||
      (proposal.invoiceId && !canReverse)
    )
      return;
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      requestKey.current ||= window.crypto.randomUUID();
      const release = proposal.mode === "release",
        replace = !!proposal.invoiceId;
      const body = {
        ...proposal.input,
        previewHash: proposal.previewHash,
        requestKey: requestKey.current,
        ...(release
          ? {
              wholeReconciliationReviewed: true,
              documentsRetainedConfirmed: true,
              noFinancialChangeConfirmed: true,
            }
          : replace
            ? {
                correctedInvoiceReviewed: true,
                originalRetainedConfirmed: true,
                noFinancialChangeConfirmed: true,
              }
            : {
                actualInvoiceReviewed: true,
                noDuplicateEntryConfirmed: true,
                noItcClaimConfirmed: true,
              }),
      };
      const url = release
        ? `${base}/invoice-credit-evidence/${proposal.invoiceId}/release`
        : replace
          ? `${base}/invoices/${proposal.invoiceId}/replacement`
          : `${base}/invoices`;
      await apiRequest(url, { method: "POST", accessToken, body });
      if (alive.current) {
        setNotice(
          release
            ? "Documentary reconciliation released. Correct credit evidence, then replace the retained invoice; do not create a duplicate invoice."
            : "Full invoice and credit evidence reconciled. Supplier dues, GST, stock and payments are unchanged.",
        );
        setProposal(null);
        setChecks({});
        requestKey.current = null;
        try {
          await onSaved?.();
        } catch (e) {
          if (alive.current)
            setError(`Reconciliation saved. Reload invoice history before another action: ${e.message}`);
        }
      }
    } catch (e) {
      if (alive.current) setError(e.message);
    } finally {
      pending.current = false;
      if (alive.current) setBusy(false);
    }
  }
  return (
    <section className="rounded-xl border border-indigo-200 p-4">
      <button
        type="button"
        disabled={busy}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="font-semibold text-indigo-900"
      >
        Full supplier invoice + separate credit notes
      </button>
      {open && (
        <div className="mt-3 space-y-4 text-sm">
          <p>
            Keep the actual invoice gross. Select whole, already-matched credit
            evidence for this one bill; only the net consumes documentary
            availability. No new expense, payable, ITC or refund. Initial
            invoice-variance rectifications and multi-bill sources remain
            unsupported here.
          </p>
          {record.snapshot.bills.length !== 1 ? (
            <p>One original supplier bill is required.</p>
          ) : (
            <fieldset disabled={busy || blocked} className="space-y-3">
              <label className="block">
                Invoice reconciliation action
                <select
                  className="mt-1 w-full rounded border p-2"
                  value={invoiceId}
                  onChange={(e) => selectInvoice(e.target.value)}
                >
                  <option value="">Reconcile a new full invoice</option>
                  {invoices
                    .filter((i) => !i.supersededBy && !i.reversal)
                    .map((i) => (
                      <option key={i.id} value={i.id}>
                        Replace retained {i.invoiceNumber}
                        {i.documentaryRelease
                          ? " · reconciliation released"
                          : ""}
                      </option>
                    ))}
                </select>
              </label>
              <button
                type="button"
                onClick={load}
                className="rounded border px-3 py-2"
              >
                Load matched credit evidence
              </button>
              <div className="rounded border bg-slate-50 p-3">
                <p className="mb-2 font-semibold">
                  Whole credit-document matches
                </p>
                {!evidence.length && (
                  <p>
                    No loaded evidence. Retain and match supplier credit notes
                    first, then load them here.
                  </p>
                )}
                {evidence.map((m) => (
                  <label key={m.id} className="mb-2 flex items-start gap-2">
                    <input
                      type="checkbox"
                      disabled={
                        !!m.usedByInvoice && m.usedByInvoice !== invoiceId
                      }
                      checked={selected.includes(m.id)}
                      onChange={(e) => {
                        setSelected((ids) =>
                          e.target.checked
                            ? [...ids, m.id]
                            : ids.filter((id) => id !== m.id),
                        );
                        invalidate();
                      }}
                    />
                    {m.number} · match ₹{m.amount} · {m.id}
                    {m.usedByInvoice && (
                      <span> · reserved by invoice {m.usedByInvoice}</span>
                    )}
                  </label>
                ))}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {Object.entries(fields).map(([key, label]) => (
                  <label key={key}>
                    {label}
                    <input
                      className="mt-1 w-full rounded border p-2"
                      type={key === "invoiceDate" ? "date" : "text"}
                      maxLength={
                        ["reason", "reviewNote"].includes(key) ? 1000 : 200
                      }
                      value={form[key]}
                      onChange={(e) => {
                        setForm((f) => ({ ...f, [key]: e.target.value }));
                        invalidate();
                      }}
                    />
                  </label>
                ))}
              </div>
              <p className="rounded bg-indigo-50 p-3">
                Actual gross ₹{gross !== null ? money(gross) : "—"} − existing
                credit evidence ₹{money(credits)} = net documentary allocation ₹
                {net || "—"}
              </p>
              <button
                type="button"
                disabled={
                  !selected.length || !net || (invoiceId && !canReverse)
                }
                onClick={() => preview("reconcile")}
                className="rounded border px-3 py-2"
              >
                Preview full invoice with credits
              </button>
              {chosen?.snapshot.creditMatches?.length &&
                !chosen.documentaryRelease &&
                canReverse && (
                  <button
                    type="button"
                    disabled={form.reason.trim().length < 20}
                    onClick={() => preview("release")}
                    className="ml-3 rounded border border-red-200 px-3 py-2 text-red-800"
                  >
                    Preview whole documentary release
                  </button>
                )}
              {error && (
                <p role="alert" className="text-red-700">
                  {error}
                </p>
              )}
              {notice && (
                <p role="status" className="text-green-800">
                  {notice}
                </p>
              )}
              {proposal && (
                <div className="space-y-3 rounded border border-amber-300 p-3">
                  <p>{proposal.warning}</p>
                  <p>
                    Full gross ₹
                    {proposal.snapshot.replacement?.total ||
                      proposal.snapshot.total ||
                      proposal.snapshot.gross}{" "}
                    · net ₹
                    {proposal.snapshot.replacement?.netAmount ||
                      proposal.snapshot.netAmount ||
                      proposal.snapshot.net}
                  </p>
                  {[
                    [
                      "one",
                      "I reviewed the actual full invoice, each credit note and their exact original invoice references.",
                    ],
                    [
                      "two",
                      "All historical documents are retained; this changes documentary reconciliation only.",
                    ],
                    [
                      "three",
                      "No supplier dues, expense, GST claim, stock, payment or refund change is authorised.",
                    ],
                  ].map(([key, label]) => (
                    <label key={key} className="block">
                      <input
                        type="checkbox"
                        checked={!!checks[key]}
                        onChange={(e) =>
                          setChecks((c) => ({ ...c, [key]: e.target.checked }))
                        }
                      />{" "}
                      {label}
                    </label>
                  ))}
                  <button
                    type="button"
                    disabled={
                      !allowed ||
                      ![checks.one, checks.two, checks.three].every(Boolean) ||
                      (proposal.invoiceId && !canReverse)
                    }
                    onClick={save}
                    className="rounded bg-indigo-800 px-4 py-2 text-white disabled:opacity-40"
                  >
                    {proposal.mode === "release"
                      ? "Release whole documentary reconciliation"
                      : "Retain full invoice reconciliation"}
                  </button>
                </div>
              )}
            </fieldset>
          )}
        </div>
      )}
    </section>
  );
}
